import { Injectable } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import {
  CurrencyPairCalendarPrimaryLookupService,
  FillOrderData,
  FillTolerancePrimaryService,
} from "shared/cubiq-services";
import { BfxConfigIDRefHelper } from "../../../shared/helper-services/bfx-config-id-ref-helper";
import * as CubiqTS from "shared/cubiq-model-multi-asset";
import { ValueDateHelper } from "../../../shared/cubiq-services/model/orders/ValueDateHelper";
import { MappedCurrencyPairCalendarImpl } from "../../../shared/cubiq-services/model/mapped/MappedCurrencyPairCalendarImpl";

@Injectable()
export class BfxFillWarningService {
  protected localeString: string = "DD-MMM-YY";
  private valueDateHelper: ValueDateHelper;

  constructor(
    private calendarService: CurrencyPairCalendarPrimaryLookupService,
    private translationService: TranslateService,
  ) {
    this.valueDateHelper = new ValueDateHelper(calendarService.getBase());
  }

  public buildValueDateMessages(orders: FillOrderData[]) {
    return orders.reduce((ids, fod) => {
      if (fod.valueDate != null) {
        ids.push({
          orderId: fod.orderId,
          orderRef: fod.orderRef,
          error: this.translationService.instant("detail.fill.value.date.warning.message", [
            fod.orderRef,
            fod.lockedValueDate.format(this.localeString),
            fod.valueDate.format(this.localeString),
          ]),
        });
      }
      return ids;
    }, []);
  }

  public async buildTenorAdjustedMessages(orders: Array<CubiqTS.OrderData> | Array<FillOrderData>) {
    let warnCurrencies: Set<string> = new Set();
    let warnArgs: Array<Array<string>> = [];

    for (let order of orders) {
      let baseCode;
      let nonBaseCode;
      let product;
      let tenor;
      let originalTenor;
      let isAdjustedForHoliday = false;

      if (order instanceof CubiqTS.OrderDataImpl) {
        baseCode = order.baseCode;
        nonBaseCode = order.nonBaseCode;
        product = order.product;
        if (order.adjustedSettleTenor != null) {
          originalTenor = order.settleTenor;
          tenor = order.adjustedSettleTenor;
          isAdjustedForHoliday = true;
        } else {
          tenor = order.settleTenor;
        }
      } else if (order instanceof FillOrderData) {
        baseCode = order.baseCcy;
        nonBaseCode = order.nonBaseCcy;
        product = order.product;
        if (order.adjustedSettleTenor != null) {
          originalTenor = order.settleTenor;
          tenor = order.adjustedSettleTenor;
          isAdjustedForHoliday = true;
        } else {
          tenor = order.settleTenor;
        }
      } else {
        continue;
      }

      if (tenor != null) {
        const calendar = await this.calendarService.getCalendarWithHolidays(
          baseCode,
          nonBaseCode,
          product,
        );
        const ttv =
          calendar instanceof MappedCurrencyPairCalendarImpl
            ? calendar?.tenorToValueDateMap?.get(tenor)
            : Array.isArray(calendar?.tenorToValueDateSet)
            ? calendar.tenorToValueDateSet.find((v) => v?.tenor === tenor)
            : null;

        if (isAdjustedForHoliday) {
          warnCurrencies.add(`${baseCode}`);
          warnCurrencies.add(`${nonBaseCode}`);
          warnArgs.push([
            `${baseCode}/${nonBaseCode}`,
            this.valueDateHelper.getTenorStr(originalTenor),
            CubiqTS.moment([ttv.valueDate.year, ttv.valueDate.month - 1, ttv.valueDate.day]).format(
              this.localeString,
            ),
          ]);
        }
      }
    }

    let key = `fill.value.date.warning.message.${warnCurrencies.size > 2 ? "pair" : "no.pair"}`;

    return [
      ...warnArgs.reduce((messages, warnArg) => {
        messages.add(this.translationService.instant(key, warnArg));
        return messages;
      }, new Set<string>()),
    ];
  }

  public addOrderRefs(orders: FillOrderData[]) {
    const postfix = orders.length > 7 ? "..." : "";
    const refs = orders
      .slice(0, 6)
      .map((o) => BfxConfigIDRefHelper.getOrderRef(o))
      .join("\n");

    return `${refs}${postfix}`;
  }

  public addFillValueDateErrorMessage(source, target) {
    let key = "detail.fill.value.date.warning.multiple.message";

    if (source.length === 1) {
      target.push(source[0].error);
    } else if (source.length > 1) {
      target.push(this.translationService.instant(key, [`${source.length}`]));
    }

    return target;
  }

  public addErrorMessages(source, target) {
    for (let message of source) {
      target.push(message);
    }

    return target;
  }
}
